import React, { useState } from 'react';
import { 
  User, Settings, Package, FileText, CreditCard, Truck, 
  ShoppingBag, BarChart3, Calendar, MapPin, ChevronRight,
  Plus, Edit, Eye, Download, Filter
} from 'lucide-react';

const MyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'profile' | 'merchant' | 'trips' | 'settings'>('profile');
  const [activeSubMenu, setActiveSubMenu] = useState<string>('');

  // 用户信息
  const userInfo = {
    name: '张三',
    phone: '138****8888',
    avatar: '👤',
    level: 'VIP会员',
    points: 2580,
    balance: 1250.50
  };

  // 商家中心子菜单
  const merchantSubMenus = [
    { id: 'products', name: '商品管理', icon: Package, count: 24, description: '管理商品信息、库存、价格等' },
    { id: 'orders', name: '订单管理', icon: FileText, count: 156, description: '查看订单状态、处理退款等' },
    { id: 'finance', name: '财务管理', icon: CreditCard, count: 0, description: '收入统计、提现、对账单等' },
    { id: 'analytics', name: '数据分析', icon: BarChart3, count: 0, description: '销售数据、用户行为分析等' },
    { id: 'marketing', name: '营销推广', icon: ShoppingBag, count: 3, description: '优惠券、活动、推广工具等' }
  ];

  // 历史行程数据
  const tripHistory = [
    {
      id: 1,
      date: '2024-01-15',
      startLocation: '深圳市南山区科技园',
      endLocation: '深圳市福田区购物公园',
      distance: '8.5 km',
      fuelConsumption: '0.8L',
      batteryConsumption: '2.1KWH',
      cost: 25.50,
      status: '已完成'
    },
    {
      id: 2,
      date: '2024-01-14',
      startLocation: '深圳市罗湖区东门',
      endLocation: '深圳市宝安区机场',
      distance: '32.1 km',
      fuelConsumption: '2.1L',
      batteryConsumption: '8.5KWH',
      cost: 89.20,
      status: '已完成'
    },
    {
      id: 3,
      date: '2024-01-13',
      startLocation: '深圳市龙岗区布吉',
      endLocation: '深圳市盐田区大梅沙',
      distance: '18.7 km',
      fuelConsumption: '1.2L',
      batteryConsumption: '4.8KWH',
      cost: 45.80,
      status: '已完成'
    }
  ];

  // 账单数据
  const billHistory = [
    {
      id: 1,
      date: '2024-01-15',
      type: '用车费用',
      amount: 25.50,
      status: '已支付',
      description: '南山区科技园 → 福田区购物公园'
    },
    {
      id: 2,
      date: '2024-01-14',
      type: '用车费用',
      amount: 89.20,
      status: '已支付',
      description: '罗湖区东门 → 宝安区机场'
    },
    {
      id: 3,
      date: '2024-01-13',
      type: '用车费用',
      amount: 45.80,
      status: '已支付',
      description: '龙岗区布吉 → 盐田区大梅沙'
    }
  ];

  const renderProfileSection = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-center space-x-4">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center text-3xl">
          {userInfo.avatar}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-800 mb-1">{userInfo.name}</h2>
          <p className="text-gray-600 mb-2">{userInfo.phone}</p>
          <div className="flex items-center space-x-4">
            <span className="px-3 py-1 bg-primary text-white text-sm rounded-full">
              {userInfo.level}
            </span>
            <span className="text-sm text-gray-500">积分: {userInfo.points}</span>
          </div>
        </div>
        <button className="p-2 text-gray-400 hover:text-gray-600">
          <Edit size={20} />
        </button>
      </div>
      
      <div className="mt-6 grid grid-cols-2 gap-4 text-center">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">¥{userInfo.balance}</div>
          <div className="text-sm text-gray-500">账户余额</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{tripHistory.reduce((sum, trip) => sum + parseFloat(trip.distance.replace(' km', '')), 0).toFixed(1)}km</div>
          <div className="text-sm text-gray-500">用车总里程(km)</div>
        </div>
        {/* <div>
          <div className="text-lg font-bold text-green-600">
            {tripHistory.reduce((sum, trip) => sum + parseFloat(trip.distance.replace(' km', '')), 0).toFixed(1)}
          </div>
          <div className="text-sm text-gray-500">总里程(km)</div>
        </div> */}
      </div>
    </div>
  );

  const renderMerchantSection = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">商家中心</h3>
        <button className="text-primary text-sm">管理店铺</button>
      </div>
      
      <div className="space-y-3">
        {merchantSubMenus.map((menu) => (
          <div
            key={menu.id}
            onClick={() => setActiveSubMenu(activeSubMenu === menu.id ? '' : menu.id)}
            className="flex items-center justify-between p-4 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <menu.icon size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="font-medium text-gray-800">{menu.name}</div>
                <div className="text-sm text-gray-500">{menu.description}</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {menu.count > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
                  {menu.count}
                </span>
              )}
              <ChevronRight 
                size={16} 
                className={`text-gray-400 transition-transform ${
                  activeSubMenu === menu.id ? 'rotate-90' : ''
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTripHistorySection = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">历史行程</h3>
        <button className="text-primary text-sm">查看全部</button>
      </div>
      
      <div className="space-y-4">
        {tripHistory.slice(0, 3).map((trip) => (
          <div key={trip.id} className="border border-gray-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">{trip.date}</span>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                trip.status === '已完成' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {trip.status}
              </span>
            </div>
            
            <div className="space-y-2 mb-3">
              <div className="flex items-center space-x-2">
                <MapPin size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">{trip.startLocation}</span>
              </div>
              <div className="flex items-center space-x-2 ml-6">
                <MapPin size={16} className="text-gray-400" />
                <span className="text-sm text-gray-600">{trip.endLocation}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">距离: {trip.distance}</span>
              <span className="text-gray-500">费用: ¥{trip.cost}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBillSection = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">账单明细</h3>
        <button className="text-primary text-sm">查看全部</button>
      </div>
      
      <div className="space-y-3">
        {billHistory.slice(0, 3).map((bill) => (
          <div key={bill.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard size={16} className="text-green-600" />
              </div>
              <div>
                <div className="font-medium text-gray-800">{bill.type}</div>
                <div className="text-sm text-gray-500">{bill.description}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-gray-800">¥{bill.amount}</div>
              <div className="text-sm text-gray-500">{bill.date}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSettingsSection = () => (
    <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">设置</h3>
      
      <div className="space-y-3">
        <button className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
          <div className="flex items-center space-x-3">
            <Settings size={20} className="text-gray-400" />
            <span className="text-gray-800">账户设置</span>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
        
        <button className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
          <div className="flex items-center space-x-3">
            <Truck size={20} className="text-gray-400" />
            <span className="text-gray-800">车辆设置</span>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
        
        <button className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
          <div className="flex items-center space-x-3">
            <CreditCard size={20} className="text-gray-400" />
            <span className="text-gray-800">支付设置</span>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 头部 */}
      <div className="bg-white border-b">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-800">我的</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 用户信息 */}
        {renderProfileSection()}
        
        {/* 商家中心 */}
        {renderMerchantSection()}
        
        {/* 历史行程 */}
        {renderTripHistorySection()}
        
        {/* 账单明细 */}
        {renderBillSection()}
        
        {/* 设置 */}
        {renderSettingsSection()}
      </div>
    </div>
  );
};

export default MyPage;
